import React from "react";
import {
  Box,
  Dialog,
  IconButton,
  Typography,
  useMediaQuery
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import {
  guidanceDialogBackdropSx,
  guidanceDialogBodySx,
  guidanceDialogFooterSx,
  guidanceDialogHeaderSx,
  guidanceDialogIconBadgeSx,
  guidanceDialogPaperSx,
  formModalScrollbarSx
} from "./guidanceDialogStyles";

export interface GuidanceDialogShellProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  headerActions?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  footerAlign?: "start" | "center" | "end";
  maxWidth?: number | "xs" | "sm" | "md" | "lg" | "xl";
  fullWidth?: boolean;
  dataTestId?: string;
  disableEscapeKeyDown?: boolean;
}

const GuidanceDialogShell: React.FC<GuidanceDialogShellProps> = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  headerActions,
  children,
  footer,
  footerAlign = "center",
  maxWidth = "sm",
  fullWidth = true,
  dataTestId,
  disableEscapeKeyDown = false
}) => {
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useKivunovaTranslation();

  const dialogMaxWidth = typeof maxWidth === "number" ? false : maxWidth;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={dialogMaxWidth}
      fullWidth={fullWidth}
      disableEscapeKeyDown={disableEscapeKeyDown}
      {...(dataTestId ? { "data-testid": dataTestId } : {})}
      slotProps={{
        backdrop: { sx: guidanceDialogBackdropSx(theme) },
        paper: {
          sx: {
            ...guidanceDialogPaperSx(),
            ...(typeof maxWidth === "number" ? { maxWidth } : {})
          }
        }
      }}
    >
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden"
        }}
      >
        <Box sx={guidanceDialogHeaderSx}>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label={t("close", "Close")}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "text.secondary"
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          {headerActions ? (
            <Box
              sx={{
                position: "absolute",
                top: 8,
                right: 44,
                display: "flex",
                gap: 0.5
              }}
            >
              {headerActions}
            </Box>
          ) : null}
          {icon ? <Box sx={guidanceDialogIconBadgeSx}>{icon}</Box> : null}
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color="text.primary"
            sx={{
              letterSpacing: "-0.02em",
              lineHeight: 1.3,
              fontSize: matchMD ? "1rem" : "1.05rem",
              pr: 4
            }}
          >
            {title}
          </Typography>
          {subtitle ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.75,
                maxWidth: 420,
                mx: "auto",
                fontSize: "0.8125rem",
                px: 1
              }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>

        <Box sx={{ ...guidanceDialogBodySx, ...formModalScrollbarSx }}>
          {children}
        </Box>

        {footer ? (
          <Box
            sx={{
              ...guidanceDialogFooterSx,
              display: "flex",
              flexDirection: matchMD ? "column" : "row",
              justifyContent:
                footerAlign === "center"
                  ? "center"
                  : footerAlign === "end"
                    ? "flex-end"
                    : "flex-start",
              alignItems:
                matchMD && footerAlign === "center" ? "center" : undefined,
              gap: 1.5
            }}
          >
            {footer}
          </Box>
        ) : null}
      </Box>
    </Dialog>
  );
};

export default GuidanceDialogShell;
